// api/estatutoApi.js
// Funciones para interactuar con la API de extracción de estatutos

import axios from "axios"

const API_BASE = window.REACT_APP_URL || ""
const BASE_PATH = `${API_BASE}/ewanapi/v1/estatutos`

/**
 * Extrae información completa de un estatuto (enviando Base64)
 *
 * @param {Object} datos - Datos de la solicitud
 * @param {string} datos.estatutoBase64 - PDF en Base64
 * @param {string} datos.cuit - CUIT de la empresa
 * @param {string} datos.razonSocial - Razón social esperada
 * @param {boolean} datos.extraerPoderes - Si debe extraer poderes adjuntos
 * @returns {Promise<Object>} Resultado de la extracción
 */
export const extraerEstatuto = async datos => {
  try {
    const response = await axios.post(`${BASE_PATH}/extraer`, datos)
    return response.data
  } catch (error) {
    console.error("Error en extraerEstatuto:", error)
    const mensaje =
      error.response?.data?.mensajeError ||
      error.message ||
      "Error al extraer estatuto"
    throw new Error(mensaje)
  }
}

/**
 * Extrae información de un estatuto subiendo el archivo directamente
 *
 * @param {File} archivo - Archivo PDF del estatuto
 * @param {string} cuit - CUIT de la empresa (opcional)
 * @param {string} razonSocial - Razón social esperada (opcional)
 * @returns {Promise<Object>} Resultado de la extracción
 */
export const extraerEstatutoArchivo = async (archivo, cuit, razonSocial) => {
  try {
    const formData = new FormData()
    formData.append("file", archivo)
    if (cuit) formData.append("cuit", cuit)
    if (razonSocial) formData.append("razonSocial", razonSocial)

    const response = await axios.post(
      `${BASE_PATH}/extraer-archivo`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    )

    return response.data
  } catch (error) {
    console.error("Error en extraerEstatutoArchivo:", error)
    const mensaje =
      error.response?.data?.mensajeError ||
      error.message ||
      "Error al extraer estatuto"
    throw new Error(mensaje)
  }
}

/**
 * Extrae solo los firmantes de un estatuto (versión liviana)
 *
 * @param {File} archivo - Archivo PDF del estatuto
 * @param {string} tipoSociedad - Tipo de sociedad (SA, SRL, etc.)
 * @returns {Promise<Array>} Lista de firmantes
 */
export const extraerFirmantes = async (archivo, tipoSociedad) => {
  try {
    const formData = new FormData()
    formData.append("file", archivo)
    if (tipoSociedad) formData.append("tipoSociedad", tipoSociedad)

    const response = await axios.post(
      `${BASE_PATH}/extraer-firmantes`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    )

    return response.data
  } catch (error) {
    console.error("Error en extraerFirmantes:", error)
    throw new Error(
      error.response?.data?.message || "Error al extraer firmantes",
    )
  }
}

/**
 * Obtiene un resumen rápido del estatuto
 *
 * @param {string} estatutoBase64 - PDF en Base64
 * @returns {Promise<Object>} Resumen del estatuto
 */
export const obtenerResumenEstatuto = async estatutoBase64 => {
  try {
    const response = await axios.post(`${BASE_PATH}/resumen`, {
      estatutoBase64,
    })
    return response.data
  } catch (error) {
    console.error("Error en obtenerResumenEstatuto:", error)
    throw new Error(error.response?.data?.message || "Error al obtener resumen")
  }
}

/**
 * Valida si una persona específica puede firmar
 *
 * @param {File} archivo - Archivo PDF del estatuto
 * @param {string} tipoSociedad - Tipo de sociedad
 * @param {string} numeroDocumento - Documento de la persona a validar
 * @returns {Promise<Object>} Resultado de la validación
 */
export const validarFirmante = async (
  archivo,
  tipoSociedad,
  numeroDocumento,
) => {
  try {
    const formData = new FormData()
    formData.append("file", archivo)
    formData.append("tipoSociedad", tipoSociedad)
    formData.append("numeroDocumento", numeroDocumento)

    const response = await axios.post(
      `${BASE_PATH}/validar-firmante`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    )

    return response.data
  } catch (error) {
    console.error("Error en validarFirmante:", error)
    throw new Error(
      error.response?.data?.mensaje || "Error al validar firmante",
    )
  }
}

/**
 * Convierte un archivo a Base64
 *
 * @param {File} file - Archivo a convertir
 * @returns {Promise<string>} Archivo en Base64
 */
export const fileToBase64 = file => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => {
      // Remover el prefijo "data:application/pdf;base64,"
      const base64 = reader.result.split(",")[1]
      resolve(base64)
    }
    reader.onerror = error => reject(error)
  })
}
